import React from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import MuiGrid from "@mui/material/Grid";
import { Box } from "@mui/material";
import { Form, Grid, useFormContext } from "@programari-limit/base-react";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import Tab from "@mui/material/Tab";
import TabPanel from "@mui/lab/TabPanel";
import GridFormField from "components/GridFormField";
import { suffixValueFormatter } from "utils/muiUtils";
import { ActionFacturarBonoVenedorContent } from "./BonosTalonari";
import {
    columnesAgendaProducte,
    columnesReserves,
    columnesTalonaris,
    columnesUsuaris,
} from "sharedAdvancedSearch/advancedSearchColumns";

const BonosTalonariFormContent = () => {
    return (
        <MuiGrid container spacing={2} sx={{ mt: 1 }}>
            <GridFormField name="numero" lg={2} />
            <GridFormField
                name="disponibilitat"
                lg={4}
                advancedSearchColumns={columnesAgendaProducte}
                advancedSearchMaxWidth="lg"
            />
            <GridFormField name="estat" lg={2} />
            <MuiGrid item lg={4} />
            <GridFormField name="adults" lg={2.25} />
            <GridFormField name="preuAdult" lg={2.25} />
            <GridFormField name="infants" lg={2.25} />
            <GridFormField name="preuInfant" lg={2.25} />
            <GridFormField name="importTotal" lg={3} />
            <GridFormField
                name="talonari"
                lg={2.5}
                advancedSearchColumns={columnesTalonaris}
                advancedSearchMaxWidth="lg"
            />
            <GridFormField name="facturaData" lg={2} />
            <GridFormField name="facturaNumero" lg={1.5} />
            <GridFormField name="facturaSerie" lg={3} />
            <GridFormField
                name="reserva"
                lg={3}
                advancedSearchColumns={columnesReserves}
                advancedSearchMaxWidth="xl"
            />
            <GridFormField name="observacions" lg={12} />
        </MuiGrid>
    );
};
const BonosTalonariDetall = () => {
    const { t } = useTranslation();
    const gridApiRef = React.useRef();
    const { id } = useParams();

    const columns = [
        { name: "numero", flex: 1 },
        { name: "disponibilitat", flex: 1 },
        { name: "adults", flex: 1 },
        { name: "preuAdult", flex: 1 },
        { name: "infants", flex: 1 },
        { name: "preuInfant", flex: 1 },
        {
            name: "importTotal",
            flex: 1,
            type: "currency",
            currencyDecimalPlaces: 2,
            formFieldProps: {
                suffix: " €",
            },
            valueFormatter: (params) => suffixValueFormatter(params),
        },
        { name: "estat", flex: 1 },
        { name: "observacions", flex: 1 },
    ];

    const rowAdditionalActions = (params) => {
        const listActions = [];
        

        if (params?.talonariVenedor) {
            listActions.push({
                icon: "point_of_sale",
                showInMenu: true,
                rowLinkAction: "TB_FACTURAR_BONO_VENEDOR",
                rowLinkActionOnSuccess: () => gridApiRef?.current?.doRefresh(),
                rowLinkFormChildren: <ActionFacturarBonoVenedorContent />,
                rowLinkFormStaticData: { talonariBonoId: params.id },
                rowLinkFormDialogSize: "lg",
                rowLinkFormInitialOnChangeRequest: true,
            });
        }

        return listActions;
    };

    return (
        <Grid
            apiRef={gridApiRef}
            title={t("bonosTalonari.title")}
            resourceName="talonariBono"
            fixedFilter={`talonari.id : ${id}`}
            columns={columns}
            actionExecEnabled
            toolbarHideCreateAction
            // rowHideEditViewAction // TODO: S'habilita de nou l'edicio, baix la responsabilitat de qui ho editi.
            rowHideDeleteAction
            rowAdditionalActions={(params) => rowAdditionalActions(params.row)}
            formPopupEditable
            formPopupComponent={<BonosTalonariFormContent />}
            formPopupTitleCreate={t("bonosTalonari.create")}
            formPopupTitleUpdate={t("bonosTalonari.update")}
            formPopupStaticData={{ talonari: id }}
            formPopupSize="lg"
            density="compact"
        />
    );
};

const TalonariFormDetall = () => {
    const { data } = useFormContext();

    return (
        <MuiGrid container spacing={2} sx={{ mt: 1 }}>
            <GridFormField name="data" lg={3} />
            <GridFormField name="primerNumero" lg={3} />
            <GridFormField name="darrerNumero" lg={3} />
            <GridFormField name="tipus" lg={3} />
            {/* Depenent del tipus hauria d'ensenyar els camps d'afiliat o be el de venedor */}
            {data?.tipus === "AFILIAT" && (
                <>
                    <GridFormField name="afiliatCodi" lg={2} />
                    <GridFormField name="afiliatNom" lg={5} />
                </>
            )}
            {data?.tipus === "VENEDOR" && (
                <GridFormField
                    name="venedor"
                    lg={5}
                    advancedSearchColumns={columnesUsuaris}
                />
            )}
            <GridFormField name="observacions" lg={12} />
        </MuiGrid>
    );
};

const TalonariFormContent = () => {
    const { t } = useTranslation();
    const { data } = useFormContext();
    const [value, setValue] = React.useState("0");

    return (
        <TabContext value={value}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <TabList
                    onChange={(event, newValue) => setValue(newValue)}
                    aria-label={t("talonaris.title")}
                >
                    <Tab
                        label={t(`talonaris.${data?.id ? "update" : "create"}`)}
                        value="0"
                    />
                    <Tab
                        label={t("bonosTalonari.title")}
                        value={"1"}
                        disabled={!data?.id}
                    />
                </TabList>
            </Box>
            <TabPanel value="0" sx={{ height: "100%", p: 0 }}>
                <TalonariFormDetall />
            </TabPanel>
            <TabPanel value="1" sx={{ height: "100%", p: 0 }}>
                <BonosTalonariDetall />
            </TabPanel>
        </TabContext>
    );
};

const TalonarisDetall = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();

    return (
        <Box sx={{ display: "flex", height: "100%", flexDirection: "column" }}>
            <Form
                resourceName="talonari"
                id={id}
                titleCreate={t("talonaris.create")}
                titleUpdate={t("talonaris.update")}
                onGoBack={() => navigate(-1)}
                // readOnly
            >
                <TalonariFormContent />
            </Form>
        </Box>
    );
};
export default TalonarisDetall;
